/** Formateo consistente de importes, fechas y conteos. */

const money = new Intl.NumberFormat("es-SV", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integer = new Intl.NumberFormat("es-SV", { maximumFractionDigits: 0 });

const compact = new Intl.NumberFormat("es-SV", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const millions = new Intl.NumberFormat("es-SV", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const percent = new Intl.NumberFormat("es-SV", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const month = new Intl.DateTimeFormat("es-SV", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/**
 * Importe contable: miles separados y negativos ENTRE PARÉNTESIS.
 *
 * Es la convención —en una columna de cifras el paréntesis se distingue de un
 * guion a simple vista y no depende del color—, y es la que ya usaban el PDF
 * (`_money_text`) y el XLSX (`#,##0.00_);(#,##0.00)`). La pantalla escribía
 * `-63,299,683.84` donde el documento firmado escribe `(63,299,683.84)`: la
 * misma cifra en dos notaciones, y la que se apartaba era la vista previa.
 */
export function formatMoney(value: unknown): string {
  const n = toNumber(value);
  if (n === undefined) return "—";
  const texto = money.format(Math.abs(n));
  return n < 0 ? `(${texto})` : texto;
}

export function formatInteger(value: unknown): string {
  const n = toNumber(value);
  return n === undefined ? "—" : integer.format(n);
}

/** Para tiles: 1,284 / 12.9K / 4.2M. Nunca en columnas de tabla. */
export function formatCompact(value: unknown): string {
  const n = toNumber(value);
  if (n === undefined) return "—";
  return Math.abs(n) < 10_000 ? integer.format(n) : compact.format(n);
}

/**
 * Importes de cabecera en millones, con la MISMA unidad en toda la fila.
 * Mezclar «1.2 B» con «17.8 M» obliga a leer el sufijo antes que la cifra y
 * destruye la comparación de un vistazo, que es lo único que una fila de
 * tiles hace bien. El importe exacto viaja en la nota, no se pierde.
 */
export function formatMillions(value: unknown): string {
  const n = toNumber(value);
  if (n === undefined) return "—";
  return `${millions.format(n / 1_000_000)} M`;
}

/** Participación sobre una base. Sin base o con base cero, no hay porcentaje. */
export function formatShare(value: unknown, base: unknown): string | undefined {
  const n = toNumber(value);
  const b = toNumber(base);
  if (n === undefined || b === undefined || b === 0) return undefined;
  return `${percent.format((n / b) * 100)} %`;
}

/**
 * Variación contra una base con nombre. Devuelve `undefined` cuando no hay
 * comparativo: es preferible no dibujar nada a dibujar una variación contra
 * cero, que siempre sale infinita y siempre miente.
 */
export function formatVariation(value: unknown, prior: unknown): string | undefined {
  const n = toNumber(value);
  const b = toNumber(prior);
  if (n === undefined || b === undefined || b === 0) return undefined;
  const change = ((n - b) / Math.abs(b)) * 100;
  const sign = change > 0 ? "+" : change < 0 ? "−" : "±";
  return `${sign}${percent.format(Math.abs(change))} %`;
}

/** «jul 2026» a partir de un mes de período (siempre el día 1). */
export function formatMonth(value: unknown): string {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return String(value);
  return month.format(date);
}

export function formatDate(value: unknown): string {
  if (!value) return "—";
  const text = String(value);
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toISOString().slice(0, 10);
}

export function formatDateTime(value: unknown): string {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().slice(0, 19).replace("T", " ") + " UTC";
}

export function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Las columnas monetarias se reconocen por nombre, igual que en el XLSX. */
const MONEY_HINTS = [
  "debit",
  "credit",
  "balance",
  "base_",
  "total_",
  "fiscal",
  "iva",
  "movement",
  "amount",
  "coefficient",
  /* El estado de flujos de efectivo. Ninguna de sus columnas casaba con las
     pistas de arriba, así que la pantalla y el Excel las escribían como
     flotantes crudos: sin separador de miles, sin dos decimales y sin el
     paréntesis del negativo, en un estado que se firma. */
  "cash_",
  "_flow",
  "operating",
  "investing",
  "financing",
  "difference",
];

export function isMoneyField(field: string): boolean {
  return MONEY_HINTS.some((hint) => field.includes(hint));
}

/**
 * Columnas de MOVIMIENTO del período: Debe y Haber.
 *
 * Se agrupan visualmente porque son un bloque semántico —lo que pasó en el
 * período— entre dos saldos que son estados: el inicial y el final. Odoo lo
 * resuelve con una banda gris vertical, y funciona: separa «actividad» de
 * «situación» sin necesidad de un encabezado que lo explique.
 */
export function isMovementField(field: string): boolean {
  return field.includes("debit") || field.includes("credit");
}

export function isDateField(field: string): boolean {
  return field.includes("date") || field.includes("period_month");
}

/** Celda de tabla ya formateada según el nombre de su columna. */
export function formatCell(field: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "sí" : "no";
  if (isDateField(field)) return formatDate(value);
  if (isMoneyField(field) && toNumber(value) !== undefined) return formatMoney(value);
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

/**
 * Razón financiera en la unidad en que se lee.
 *
 * El ×100 vive AQUÍ y solo aquí. El motor almacena siempre una fracción, así
 * que no hay forma de que dos indicadores acaben en unidades distintas — que
 * es el defecto que hoy tienen ROA y ROE en la hoja de cálculo, calculados
 * como `(x/y)*100` mientras el margen bruto devuelve la fracción.
 */
export function formatRatio(
  value: unknown,
  format: "money" | "ratio" | "percent",
): string {
  const n = toNumber(value);
  if (n === undefined) return "—";
  if (format === "percent") return `${percent.format(n * 100)} %`;
  if (format === "money") return money.format(n);
  return n.toLocaleString("es-SV", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

/** Participación ya calculada (fracción) sobre su base. */
export function formatPercentOfBase(value: unknown): string {
  const n = toNumber(value);
  return n === undefined ? "—" : `${percent.format(n * 100)} %`;
}


/**
 * Clase de una celda de importe, decidida por su VALOR y no por su columna.
 *
 * El negativo se dice con color además de con el paréntesis —que es la notación
 * del documento firmado—, y el cero se apaga. En un balance con cuatrocientas
 * cuentas, doscientos «0.00» a plena tinta tapan las tres cifras que importan;
 * apagado sigue estando para quien lo busque, pero deja de competir.
 *
 * No oculta nada y no redondea nada: solo baja el contraste.
 */
export function moneyClass(value: unknown): string {
  const n = toNumber(value);
  if (n === undefined) return "num";
  if (n < 0) return "num neg";
  return n === 0 ? "num cero" : "num";
}
